import React from "react";
import * as XLSX from "xlsx";
import _ from "lodash";
import AppContext from "../../../AppContext";
import {
  getCurrentOrder,
  getDeliveryDataForProduct,
  getDeliveryPeriodsForEan,
  getProductForEan,
} from "../../../selectors";
import { generateProductId } from "../../../utils/products";
import { getTranslateByScope } from "../../../i18n";

const shouldAddNoDeliveryDateProducts = true;

const useUploadXLS = () => {
  const translate = getTranslateByScope("app.currentOrder");
  const [xlsData, setXlsData] = React.useState();
  const context = React.useContext(AppContext);
  const currentOrder = getCurrentOrder(context);

  const onFileUpload = (e) => {
    if (!currentOrder.doc.defaultDeliveryDate) {
      context.actions.setToast({
        title: translate("fileUploadToast.noDeliveryDateTitle"),
        text: translate("fileUploadToast.noDeliveryDateDesc"),
        timeout: 10000,
      });
      return;
    }
    const [file] = e.target.files;
    const reader = new FileReader();

    reader.onload = (evt) => {
      const bstr = evt.target.result;
      const wb = XLSX.read(bstr, { type: "binary" });
      const wsname = wb.SheetNames[0];
      const ws = wb.Sheets[wsname];
      const data = XLSX.utils.sheet_to_json(ws, { header: 1 });
      setXlsData(data);
    };
    reader.readAsBinaryString(file);
  };

  const generateTimeStamp = React.useCallback(() => {
    const date = new Date();
    const day = date.getDate();
    const month = date.getMonth();
    const year = date.getFullYear();
    const hour = date.getHours();
    const minutes = date.getMinutes();
    const seconds = date.getSeconds();

    const string = `${day}.${month + 1}.${year}-${hour}:${minutes}:${seconds}`;
    return string;
  }, []);

  const downloadXLSX = React.useCallback(
    (processedOrderData) => {
      /**
       * EAN is treated as a number in JS so when we
       * export to XLSX it formats as scientific number
       * if we convert it to a string it will be shown in it's original value
       */
      const mapped = processedOrderData.map((x) => {
        const temp = { ...x };
        temp[0] = x[0].toString();
        return temp;
      });
      const heading = [
        [
          "EAN",
          "Quantity",
          "Status",
          "Note",
          "Collection",
          "Material Number",
          "Model Number",
          "Product Name",
          "Color",
          "Size",
        ],
      ];
      const ws = XLSX.utils.json_to_sheet(mapped);
      const wb = XLSX.utils.book_new();
      XLSX.utils.sheet_add_aoa(ws, heading, { origin: "A1" });
      XLSX.utils.book_append_sheet(wb, ws, "Data");
      const timestamp = generateTimeStamp();
      XLSX.writeFileXLSX(wb, `upload_${timestamp}.xlsx`);
    },
    [generateTimeStamp]
  );

  const handleNotification = React.useCallback(
    (processedOrderData) => {
      const recordsAsProducts = processedOrderData.filter((record) => record);
      const addableProducts = recordsAsProducts.filter((i) => i.deliveryDate);
      let description = "";

      if (addableProducts.length) {
        description +=
          addableProducts.length +
          translate("fileUploadToast.successfulImports") +
          "\n \n";
      }

      if (processedOrderData.length) {
        description +=
          processedOrderData.length +
          translate("fileUploadToast.partialImports") +
          "\n \n";
      }

      if (processedOrderData.length - recordsAsProducts.length) {
        description +=
          processedOrderData.length -
          recordsAsProducts.length +
          translate("fileUploadToast.unSuccessfulImports");
      }
      return description;
    },
    [translate]
  );

  const addXLSDataToOrder = () => {
    //a single row for an item consists of the EAN (index 0) and the quantity (index 1)
    //remove any records who's first item doesn't contain a valid ean (isn't a number);
    const properRecords = xlsData.filter((row) => parseInt(row[0]));
    const downloadCopy = [...properRecords];

    const productsWithoutData = properRecords.map((row, i) => {
      const ean = row[0];

      const product = getProductForEan(context)(ean.toString());
      downloadCopy[i][2] = "success";

      if (!product.data.length) {
        downloadCopy[i][2] = "failed";
        downloadCopy[i][3] = "Product not available";
        return null;
      }
      const productNumber = product.data[0].n;

      const { includesDeliveryPeriod, defaultDeliveryDate } =
        getDeliveryDataForProduct(context, productNumber);

      const deliveryPeriods = getDeliveryPeriodsForEan({ context, ean });

      if (
        !deliveryPeriods ||
        deliveryPeriods[defaultDeliveryDate.value] === undefined
      ) {
        downloadCopy[i][2] = "failed";
        downloadCopy[i][3] = "Delivery date not available";
        return null;
      }

      downloadCopy[i][3] = "Available as requested";
      downloadCopy[i][4] = product.data[0].col;
      downloadCopy[i][5] = product.data[0].mat;
      downloadCopy[i][6] = product.data[0].n;
      downloadCopy[i][7] = product.data[0].name;
      downloadCopy[i][8] = product.data[0][ean].c;
      downloadCopy[i][9] = product.data[0][ean].s;

      if (deliveryPeriods && deliveryPeriods[defaultDeliveryDate.value] === 1) {
        downloadCopy[i][3] = "Quantity limited";
      }
      if (deliveryPeriods && deliveryPeriods[defaultDeliveryDate.value] === 2) {
        downloadCopy[i][3] = "Delivery date not available";
      }

      if (
        shouldAddNoDeliveryDateProducts === false &&
        includesDeliveryPeriod === false
      ) {
        downloadCopy[i][2] = "failed";
        return null;
      }

      return {
        id: generateProductId(),
        number: productNumber,
        tischOffensiveId: null,
        createdAt: new Date(),
        deliveryDate: includesDeliveryPeriod ? defaultDeliveryDate : null,
        season: currentOrder.doc.season,
        data: {},
      };
    });

    const removedNulls = _.filter(productsWithoutData, (item) => item !== null);
    const uniqueProducts = _.uniqBy(removedNulls, "number");

    properRecords.forEach((row) => {
      const ean = row[0];
      const product = getProductForEan(context)(ean.toString());
      if (!product.data[0]) {
        return;
      }
      const entry = uniqueProducts.find((x) => x.number === product.data[0].n);
      if (!entry) {
        return;
      }
      const newData = { ...entry.data };
      const productDataItem = product.data[0][ean];

      if (productDataItem) {
        if (!newData[productDataItem.l]) {
          newData[productDataItem.l] = {};
        }

        if (!newData[productDataItem.l][productDataItem.c]) {
          newData[productDataItem.l][productDataItem.c] = {};
        }

        newData[productDataItem.l][productDataItem.c][productDataItem.s] =
          parseInt(row[1]);
      }

      entry.data = newData;
      const index = uniqueProducts.findIndex(
        (x) => x.number === product.data[0].n
      );
      uniqueProducts[index] = entry;
    });

    const description = handleNotification(uniqueProducts);

    context.actions.onAddMultipleProductsToOrder({
      order: currentOrder.doc,
      products: uniqueProducts,
      onSuccess: () =>
        context.actions.setToast({
          title: translate("fileUploadToast.title"),
          text: description,
        }),
    });
    downloadXLSX(downloadCopy);
  };

  React.useEffect(() => {
    if (xlsData) {
      addXLSDataToOrder();
    }
  }, [xlsData]); // eslint-disable-line
  return { onFileUpload };
};

export default useUploadXLS;
